/* [04] Lookbook: isolated story navigation; purchasing remains an explicit action. */
(() => {
  if (customElements.get('lookbook-stories-04')) return;

  class LookbookStories04 extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('[data-look-dialog]');
      this.stage = this.querySelector('[data-look-stage]');
      this.templates = [...this.querySelectorAll('template[data-look-slide]')];
      if (!this.dialog || !this.stage || !this.templates.length) return;
      this.index = 0;
      this.elapsed = 0;
      this.userPaused = false;
      this.holding = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.progress = this.querySelector('[data-look-progress]');
      this.buildProgress();
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.shopping ? this.setShopping(false) : this.close();
      }, options);
      this.dialog.addEventListener('close', () => this.cleanupOpen(), options);
      this.dialog.addEventListener('keydown', (event) => this.onKey(event), options);
      this.stage.addEventListener('pointerdown', (event) => this.pointerDown(event), options);
      window.addEventListener('pointerup', (event) => this.pointerUp(event), options);
      window.addEventListener('pointercancel', () => { this.suppressClickUntil = performance.now() + 500; this.clearPointer(); }, options);
      window.addEventListener('blur', () => { this.windowBlurred = true; this.clearPointer(); }, options);
      window.addEventListener('focus', () => { this.windowBlurred = false; this.lastFrame = 0; }, options);
      document.addEventListener('visibilitychange', () => { this.lastFrame = 0; this.clearPointer(); }, options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.lastFrame = 0;
        this.updatePause();
      }, options);
      this.updatePause();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanupOpen();
      this.abort?.abort();
      this.abort = null;
    }

    get shopping() { return this.dialog?.dataset.shopping === 'true'; }
    get paused() {
      return this.userPaused || this.holding || this.shopping || document.hidden || this.windowBlurred;
    }

    buildProgress() {
      if (!this.progress) return;
      this.progress.replaceChildren();
      this.templates.forEach((template, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.lookJump = String(index);
        button.className = 'l04-progress-item';
        button.setAttribute('aria-label', template.dataset.lookLabel || String(index + 1));
        const fill = document.createElement('span');
        fill.className = 'l04-progress-fill';
        fill.setAttribute('aria-hidden', 'true');
        button.append(fill);
        this.progress.append(button);
      });
    }

    onClick(event) {
      const button = event.target.closest('button, a, [data-look-open]');
      if (button?.matches('[data-look-open]')) {
        event.preventDefault();
        this.open(Number(button.dataset.lookIndex) || 0, button);
        return;
      }
      if (!this.dialog.open) return;
      if (button?.matches('[data-look-close]')) this.close();
      else if (button?.matches('[data-look-prev]')) this.go(this.index - 1);
      else if (button?.matches('[data-look-next]')) this.go(this.index + 1);
      else if (button?.matches('[data-look-shop]')) this.setShopping(true);
      else if (button?.matches('[data-look-back]')) this.setShopping(false);
      else if (button?.matches('[data-look-pause]')) {
        this.userPaused = !this.userPaused;
        this.updatePause();
      } else if (button?.matches('[data-look-jump]')) this.go(Number(button.dataset.lookJump));
      else if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      } else if (!this.shopping && !this.isInteractive(event.target) && this.inMedia(event.target)) {
        if (performance.now() < (this.suppressClickUntil || 0)) return;
        const rect = (this.stage.querySelector('[data-look-media]') || this.stage).getBoundingClientRect();
        this.go(this.index + (event.clientX < rect.left + rect.width / 2 ? -1 : 1));
      }
    }

    open(index, opener) {
      if (this.dialog.open) return;
      this.opener = opener;
      // Respect reduced motion on every opening; the visitor can explicitly play.
      this.userPaused = this.motion.matches || this.dataset.lookAutoplay === 'false';
      this.windowBlurred = false;
      this.lockPage();
      this.dialog.dataset.shopping = 'false';
      this.go(index);
      this.dialog.showModal();
      this.dialog.querySelector('[data-look-close]')?.focus({ preventScroll: true });
      this.lastFrame = 0;
      this.raf = requestAnimationFrame((time) => this.tick(time));
    }

    close() { if (this.dialog?.open) this.dialog.close(); }

    cleanupOpen() {
      cancelAnimationFrame(this.raf);
      this.clearPointer();
      this.unlockPage();
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    go(index) {
      const bounded = Math.max(0, Math.min(this.templates.length - 1, index));
      if (!Number.isFinite(bounded)) return;
      this.index = bounded;
      this.elapsed = 0;
      this.lastFrame = 0;
      this.dialog.dataset.shopping = 'false';
      this.stage.replaceChildren(this.templates[this.index].content.cloneNode(true));
      this.dialog.querySelectorAll('[data-look-title]').forEach((title) => {
        title.textContent = this.templates[this.index].dataset.lookLabel || '';
      });
      this.stage.scrollTop = 0;
      this.duration = Math.max(2000, Number(this.templates[this.index].dataset.lookDuration) || Number(this.dataset.lookDuration) || 7000);
      this.querySelectorAll('[data-look-prev]').forEach((button) => { button.disabled = this.index === 0; });
      this.querySelectorAll('[data-look-next]').forEach((button) => { button.disabled = this.index === this.templates.length - 1; });
      this.updateShoppingControls();
      this.paintProgress();
      this.dispatchEvent(new CustomEvent('lookbook:slide', { bubbles: true, detail: { index: this.index } }));
    }

    setShopping(value) {
      if (this.shopping === value) return;
      this.clearPointer();
      this.dialog.dataset.shopping = String(value);
      this.lastFrame = 0;
      this.updateShoppingControls();
      const target = value ? this.stage.querySelector('[data-look-products]') : this.dialog.querySelector('[data-look-shop]');
      if (target) {
        if (value && !target.hasAttribute('tabindex')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('lookbook:shopping', { bubbles: true, detail: { open: value } }));
    }

    updateShoppingControls() {
      this.dialog.querySelectorAll('[data-look-shop]').forEach((button) => button.setAttribute('aria-expanded', String(this.shopping)));
      this.stage.querySelectorAll('[data-look-products]').forEach((panel) => { panel.inert = !this.shopping; });
      this.updatePause();
    }

    updatePause() {
      if (!this.dialog) return;
      this.dialog.dataset.paused = String(Boolean(this.paused));
      this.querySelectorAll('[data-look-pause]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.userPaused));
        const label = this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause;
        if (label) {
          button.setAttribute('aria-label', label);
          button.textContent = label;
        }
        button.dataset.paused = String(this.userPaused);
      });
    }

    onKey(event) {
      if (this.shopping || this.isInteractive(event.target) && /^(INPUT|SELECT|TEXTAREA)$/.test(event.target.tagName)) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }

    isInteractive(target) {
      return Boolean(target.closest('a, button, input, select, textarea, label, [contenteditable], [data-look-products]'));
    }

    inMedia(target) {
      const media = this.stage.querySelector('[data-look-media]');
      return media ? media.contains(target) : this.stage.contains(target);
    }

    pointerDown(event) {
      if (!this.dialog.open || this.shopping || !event.isPrimary || event.button !== 0 || this.isInteractive(event.target) || !this.inMedia(event.target)) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() };
      this.holding = true;
      this.updatePause();
    }

    pointerUp(event) {
      const start = this.pointer;
      if (!start || event.pointerId !== start.id) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const held = performance.now() - start.time;
      this.clearPointer();
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.suppressClickUntil = performance.now() + 500;
        this.go(this.index + (dx < 0 ? 1 : -1));
      } else if (dy < -55 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        this.suppressClickUntil = performance.now() + 500;
        this.setShopping(true);
      } else if (held > 250 || Math.abs(dx) > 12 || Math.abs(dy) > 12) this.suppressClickUntil = performance.now() + 500;
    }

    clearPointer() {
      this.pointer = null;
      this.holding = false;
      this.lastFrame = 0;
      this.updatePause();
    }

    tick(time) {
      if (!this.dialog.open || !this.isConnected) return;
      if (!this.paused && this.lastFrame) this.elapsed += Math.min(time - this.lastFrame, 100);
      this.lastFrame = time;
      if (this.elapsed >= this.duration && this.index < this.templates.length - 1) this.go(this.index + 1);
      this.paintProgress();
      this.raf = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }

    paintProgress() {
      this.progress?.querySelectorAll('[data-look-jump]').forEach((bar, index) => {
        const amount = index < this.index ? 1 : index === this.index ? Math.min(1, this.elapsed / this.duration) : 0;
        bar.style.setProperty('--look-progress', String(amount));
        if (bar.firstElementChild) bar.firstElementChild.style.transform = `scaleX(${amount})`;
        if (index === this.index) bar.setAttribute('aria-current', 'step');
        else bar.removeAttribute('aria-current');
      });
    }

    lockPage() {
      if (this.bodyState) return;
      const body = document.body;
      this.scrollY = window.scrollY;
      this.bodyState = {};
      ['position', 'top', 'left', 'right', 'width', 'overflow'].forEach((key) => { this.bodyState[key] = body.style[key]; });
      Object.assign(body.style, { position: 'fixed', top: `-${this.scrollY}px`, left: '0', right: '0', width: '100%', overflow: 'hidden' });
    }

    unlockPage() {
      if (!this.bodyState) return;
      Object.assign(document.body.style, this.bodyState);
      this.bodyState = null;
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });
    }
  }

  customElements.define('lookbook-stories-04', LookbookStories04);
})();
